/**
 * Staff geometry. Converts between screen coordinates, staff positions, and pitches.
 *
 * A "staff position" is one diatonic step = half a staff space. Position 0 is the
 * bottom line of the five-line staff, positive is upward.
 */

import { diatonicStep, pitchFromDiatonicStep, type Pitch } from './pitch';

export const CLEFS = ['treble', 'bass', 'alto', 'tenor'] as const;
export type Clef = (typeof CLEFS)[number];

export interface ClefInfo {
  id: Clef;
  label: string;
  /** Unicode clef glyph, used for the ink-layer staff drawing. */
  glyph: string;
  /** The pitch sitting on the bottom line of the staff. */
  bottomLinePitch: Pitch;
  /** Comfortable octave for a chord root written in this clef. */
  defaultRootOctave: number;
  blurb: string;
}

export const CLEF_INFO: Record<Clef, ClefInfo> = {
  treble: {
    id: 'treble',
    label: 'Treble',
    glyph: '𝄞',
    bottomLinePitch: { letter: 'E', alter: 0, octave: 4 },
    defaultRootOctave: 4,
    blurb: 'G clef — the curl wraps the G4 line. Home for right hand, voice, and most lead sheets.',
  },
  bass: {
    id: 'bass',
    label: 'Bass',
    glyph: '𝄢',
    bottomLinePitch: { letter: 'G', alter: 0, octave: 2 },
    defaultRootOctave: 3,
    blurb: 'F clef — the two dots straddle the F3 line. Home for left hand, bass, and chord roots.',
  },
  alto: {
    id: 'alto',
    label: 'Alto',
    glyph: '𝄡',
    bottomLinePitch: { letter: 'F', alter: 0, octave: 3 },
    defaultRootOctave: 4,
    blurb: 'C clef centred on the middle line — that line is middle C. Viola territory.',
  },
  tenor: {
    id: 'tenor',
    label: 'Tenor',
    glyph: '𝄡',
    bottomLinePitch: { letter: 'D', alter: 0, octave: 3 },
    defaultRootOctave: 3,
    blurb: 'C clef moved up a step — middle C sits on the fourth line. Cello and trombone upper range.',
  },
};

/** Diatonic step of the bottom staff line for a clef. */
export function bottomLineStep(clef: Clef): number {
  return diatonicStep(CLEF_INFO[clef].bottomLinePitch);
}

/** Staff position (0 = bottom line, +1 per diatonic step) -> pitch. */
export function positionToPitch(clef: Clef, position: number, alter = 0): Pitch {
  return pitchFromDiatonicStep(bottomLineStep(clef) + position, alter);
}

/** Pitch -> staff position. */
export function pitchToPosition(clef: Clef, pitch: Pitch): number {
  return diatonicStep(pitch) - bottomLineStep(clef);
}

export interface StaffLayout {
  /** Vertical distance between adjacent staff lines, in CSS pixels. */
  lineGap: number;
  /** y of the bottom (fifth) line. */
  bottomLineY: number;
  /** x where the five lines start. */
  startX: number;
  /** x where the five lines end. */
  endX: number;
  /** x where writing should begin — right of the clef and key signature. */
  writingX: number;
}

export function makeLayout(width: number, height: number): StaffLayout {
  // Four gaps between five lines; keep the staff around a third of the panel height
  // so there is room for ledger lines above and below.
  const lineGap = Math.max(18, Math.min(34, Math.round(height / 11)));
  const centerY = Math.round(height / 2);
  return {
    lineGap,
    bottomLineY: centerY + lineGap * 2,
    startX: 24,
    endX: Math.max(200, width - 24),
    writingX: 24 + Math.round(lineGap * 4.5),
  };
}

/** y pixel for a staff position. */
export function positionToY(layout: StaffLayout, position: number): number {
  return layout.bottomLineY - (position * layout.lineGap) / 2;
}

/** y pixel -> nearest staff position (rounds to the nearest line or space). */
export function yToPosition(layout: StaffLayout, y: number): number {
  return Math.round(((layout.bottomLineY - y) * 2) / layout.lineGap);
}

/** Continuous version, used by recognition so we can measure how confident a snap was. */
export function yToExactPosition(layout: StaffLayout, y: number): number {
  return ((layout.bottomLineY - y) * 2) / layout.lineGap;
}

/**
 * Ledger line positions needed for a note at the given staff position.
 *
 * The staff occupies positions 0-8. The first space beyond it (9 above, -1 below)
 * needs no line; ledgers start at 10 and -2 and continue outward in twos.
 */
export function ledgerPositions(position: number): number[] {
  const out: number[] = [];
  for (let p = 10; p <= position; p += 2) out.push(p);
  for (let p = -2; p >= position; p -= 2) out.push(p);
  return out;
}
